(() => {
  if (window.tcrFacetRecoveryLoaded) return
  window.tcrFacetRecoveryLoaded = true

  const cache = new Map()
  const menuStates = new Map()
  const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric: true })
  let generation = 0

  function dataUrl(source, field) {
    const url = new URL(source)
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') && (field === null || key === field || url.searchParams.get(key) === '')) {
        url.searchParams.delete(key)
      }
    }
    url.searchParams.delete('page')
    url.searchParams.delete('sections')
    url.searchParams.set('section_id', 'tcr-facet-data')
    return url
  }

  async function readData(source, collectionId) {
    const url = new URL(source)
    const rows = []
    const ids = new Set()
    let page = 1
    let pages = 1
    do {
      const response = await fetch(url, { credentials: 'same-origin' })
      if (!response.ok) throw new Error(`Facet data request failed (${response.status})`)
      const html = new DOMParser().parseFromString(await response.text(), 'text/html')
      const script = html.querySelector('[data-tcr-facet-data]')
      if (!script) throw new Error('Facet data is unavailable')
      const data = JSON.parse(script.textContent)
      if (data.collectionId !== collectionId || data.page !== page || !Array.isArray(data.products)) {
        throw new Error('Unexpected facet data')
      }
      pages = data.pages
      if (!Number.isInteger(pages) || pages < 0 || pages > 20) throw new Error('Invalid facet pagination')
      for (const row of data.products) {
        if (typeof row.id !== 'string' || !row.values || ids.has(row.id)) throw new Error('Invalid facet product')
        ids.add(row.id)
        rows.push(row)
      }
      page += 1
      url.searchParams.set(data.pageParam || 'page', String(page))
    } while (page <= pages)
    return rows
  }

  function loadData(url, collectionId) {
    const key = `${collectionId}:${url.href}`
    if (!cache.has(key)) {
      const request = readData(url, collectionId).catch(error => {
        cache.delete(key)
        throw error
      })
      cache.set(key, request)
      if (cache.size > 24) cache.delete(cache.keys().next().value)
    }
    return cache.get(key)
  }

  function countsFor(rows, key) {
    const counts = new Map()
    for (const row of rows) {
      const value = row.values[key]
      if (typeof value !== 'string') throw new Error('Invalid facet value')
      if (value !== '') counts.set(value, (counts.get(value) || 0) + 1)
    }
    return counts
  }

  function searchableMenu(list, collectionId, key) {
    const stateKey = `${collectionId}:${key}`
    if (!menuStates.has(stateKey)) menuStates.set(stateKey, { query: '', limit: 20 })
    const state = menuStates.get(stateKey)
    const content = list.parentElement
    const widget = list.closest('.m-filter--widget')
    const title = widget?.querySelector('.m-filter--widget-title > span')?.textContent.trim() || key.replaceAll('_', ' ')
    list.dataset.facetsSearchable = 'true'
    list.id = `Tcr-Choices-${collectionId}-${key}`
    let controls = content.querySelector('[data-tcr-choice-search]')
    if (!controls) {
      controls = document.createElement('div')
      controls.className = 'tcr-facet-search'
      controls.dataset.tcrChoiceSearch = ''
      const label = document.createElement('label')
      label.textContent = 'Search choices'
      label.htmlFor = `${list.id}-search`
      const row = document.createElement('div')
      row.className = 'tcr-facet-search__row'
      const search = document.createElement('input')
      search.type = 'search'
      search.id = label.htmlFor
      search.autocomplete = 'off'
      search.placeholder = 'Type a value'
      search.setAttribute('aria-label', `Search ${title} choices`)
      search.setAttribute('aria-controls', list.id)
      search.value = state.query
      const clear = document.createElement('button')
      clear.type = 'button'
      clear.textContent = 'Clear'
      clear.className = 'tcr-facet-search__clear'
      clear.setAttribute('aria-label', `Clear search in ${title}`)
      const status = document.createElement('p')
      status.className = 'tcr-facet-search__status'
      status.setAttribute('role', 'status')
      const empty = document.createElement('p')
      empty.className = 'tcr-facet-search__empty'
      empty.textContent = 'No choices match your search.'
      empty.dataset.tcrChoiceEmpty = ''
      const more = document.createElement('button')
      more.type = 'button'
      more.className = 'tcr-facet-search__more'
      more.dataset.tcrChoiceMore = ''
      more.setAttribute('aria-controls', list.id)
      row.append(search, clear)
      controls.append(label, row, status)
      list.before(controls)
      list.after(empty, more)
      search.addEventListener('input', event => {
        // Searching choices must not submit the native product filter form.
        event.stopPropagation()
        state.query = search.value
        state.limit = 20
        list.scrollTop = 0
        updateMenu()
      })
      search.addEventListener('change', event => event.stopPropagation())
      search.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation() }
      })
      clear.addEventListener('click', event => {
        event.stopPropagation()
        state.query = ''
        state.limit = 20
        search.value = ''
        list.scrollTop = 0
        updateMenu()
        search.focus({ preventScroll: true })
      })
      more.addEventListener('click', event => {
        event.stopPropagation()
        state.limit += 20
        updateMenu()
      })
      list.addEventListener('change', updateMenu)
    }
    function updateMenu() {
      const query = state.query.trim().normalize('NFKC').toLocaleLowerCase()
      const items = [...list.children]
      const matches = items.filter(item => item.querySelector('.m-facet--label').textContent.normalize('NFKC').toLocaleLowerCase().includes(query))
      const visible = new Set(matches.slice(0, state.limit))
      for (const item of items) if (item.querySelector('input').checked) visible.add(item)
      for (const item of items) item.hidden = !visible.has(item)
      controls.querySelector('[role="status"]').textContent = query
        ? `${matches.length} matching ${matches.length === 1 ? 'choice' : 'choices'}`
        : `${visible.size} of ${items.length} choices shown`
      controls.querySelector('button').hidden = !state.query
      const more = content.querySelector('[data-tcr-choice-more]')
      const remaining = matches.filter(item => item.hidden).length
      more.hidden = remaining === 0
      more.textContent = `Show ${Math.min(20, remaining)} more`
      content.querySelector('[data-tcr-choice-empty]').hidden = matches.length !== 0
      if (widget?.classList.contains('open')) widget.style.setProperty('--content-max-height', `${content.scrollHeight}px`)
    }
    updateMenu()
  }

  function recoverActiveChoices(param, applied, source, labels, domainCounts, title) {
    const native = document.querySelector('#ActiveFacets')
    if (!native) return
    let group = document.querySelector('[data-tcr-active-recovery]')
    for (const item of group?.querySelectorAll('[data-tcr-active-param]') || []) {
      if (item.dataset.tcrActiveParam === param) item.remove()
    }
    const valuesIn = url => url.searchParams.getAll(param).flatMap(value => domainCounts.has(value) ? [value] : value.split(','))
    function appendLink(url, text, value) {
      if (!group) {
        group = document.createElement('div')
        group.className = 'm-active-facets m:flex m:flex-wrap m:items-center'
        group.dataset.tcrActiveRecovery = ''
        native.after(group)
      }
      const item = document.createElement('div')
      item.className = 'm-active-facets--item'
      const link = document.createElement('a')
      link.href = url.href
      link.textContent = text
      if (value !== null) {
        item.dataset.tcrActiveParam = param
        link.setAttribute('aria-label', `Remove ${title}: ${text}`)
        const icon = document.createElement('span')
        icon.className = 'm-active-facets--remove-icon'
        icon.setAttribute('aria-hidden', 'true')
        icon.textContent = '×'
        link.append(icon)
      } else {
        item.dataset.tcrClearAll = ''
        link.className = 'm-active-facets--remove-all'
      }
      item.append(link)
      group.append(item)
    }
    for (const value of applied) {
      const remaining = [...applied].filter(other => other !== value)
      const exists = [...native.querySelectorAll('a:not(.m-active-facets--remove-all)')].some(link => {
        const nativeValues = valuesIn(new URL(link.href, source))
        return nativeValues.length === remaining.length && nativeValues.every(v => remaining.includes(v))
      })
      if (exists) continue
      const url = new URL(source)
      url.searchParams.delete(param)
      url.searchParams.delete('page')
      for (const other of remaining) url.searchParams.append(param, other)
      appendLink(url, labels.get(value) || value, value)
    }
    group?.querySelector('[data-tcr-clear-all]')?.remove()
    if (group?.querySelector('[data-tcr-active-param]') && !native.querySelector('.m-active-facets--remove-all')) {
      const url = new URL(source)
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key)
      appendLink(url, 'Clear all', null)
    }
    if (group && !group.children.length) group.remove()
  }

  function render(list, domainRows, matchingRows, source, collectionId) {
    if (!list.isConnected || location.href !== source) return
    const param = list.dataset.tcrFacetRecovery
    const key = param.slice('filter.p.m.custom.'.length)
    const domainCounts = countsFor(domainRows, key)
    if (!domainCounts.size) return
    const counts = countsFor(matchingRows, key)
    const existing = new Map([...list.querySelectorAll('input')].map(input => [input.value, input]))
    const labels = new Map([...existing].map(([value, input]) => [value, input.parentElement.querySelector('.m-facet--label')?.textContent || value]))
    const selected = new Set()
    for (const value of new URL(source).searchParams.getAll(param)) {
      // A stored value can itself contain commas, for example "40,000 gallons".
      if (domainCounts.has(value) || existing.has(value)) selected.add(value)
      else for (const part of value.split(',')) if (domainCounts.has(part) || existing.has(part)) selected.add(part)
    }
    const applied = new Set(selected)
    for (const [value, input] of existing) {
      if (input.checked) selected.add(value)
      else selected.delete(value)
    }
    const values = [...new Set([...domainCounts.keys(), ...existing.keys(), ...selected])]
    const numeric = list.dataset.numeric === 'true'
    values.sort((a, b) => {
      const empty = Number(!counts.get(a)) - Number(!counts.get(b))
      if (empty) return empty
      const left = Number(a)
      const right = Number(b)
      return numeric && Number.isFinite(left) && Number.isFinite(right)
        ? left - right || collator.compare(a, b)
        : collator.compare(a, b)
    })
    const focusValue = list.contains(document.activeElement) ? document.activeElement.value : null
    const scrollTop = list.scrollTop
    const items = values.map((value, index) => {
      const count = counts.get(value) || 0
      const checked = selected.has(value)
      const disabled = count === 0 && !checked
      const item = document.createElement('li')
      item.className = 'm-facet--item'
      const label = document.createElement('label')
      label.className = `m-facet--checkbox${disabled ? ' m-facet--checkbox__disabled' : ''}`
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.name = param
      input.value = value
      input.id = `Tcr-Facet-${collectionId}-${key}-${index + 1}`
      input.checked = checked
      input.disabled = disabled
      label.htmlFor = input.id
      const box = document.createElement('span')
      box.className = 'm-facet--checkbox-box'
      const text = document.createElement('span')
      text.className = 'm-facet--label'
      text.textContent = labels.get(value) || value
      label.append(input, box, text)
      if (list.dataset.showCounts === 'true') {
        const badge = document.createElement('span')
        badge.className = 'm-facet--product-count'
        badge.textContent = String(count)
        label.append(badge)
      }
      item.append(label)
      return item
    })
    list.replaceChildren(...items)
    list.scrollTop = scrollTop
    if (focusValue !== null) [...list.querySelectorAll('input')].find(input => input.value === focusValue)?.focus({ preventScroll: true })
    list.dataset.facetsReady = 'true'
    const recoveredGroup = list.closest('[data-tcr-missing-facet]')
    if (recoveredGroup) {
      recoveredGroup.hidden = false
      recoveredGroup.style.removeProperty('display')
    }
    if (domainCounts.size > 100) {
      searchableMenu(list, collectionId, key)
      const title = list.closest('.m-filter--widget')?.querySelector('.m-filter--widget-title > span')?.textContent.trim() || key
      recoverActiveChoices(param, applied, source, labels, domainCounts, title)
    }
  }

  function refresh() {
    const wrapper = document.querySelector('[data-tcr-facet-collection]')
    if (!wrapper) return
    const collectionId = wrapper.dataset.tcrFacetCollection
    const lists = [...wrapper.querySelectorAll('ul[data-tcr-facet-recovery]')]
    if (!lists.length) return
    const requestGeneration = ++generation
    const source = location.href
    const domain = loadData(dataUrl(source, null), collectionId)
    for (const list of lists) {
      Promise.all([domain, loadData(dataUrl(source, list.dataset.tcrFacetRecovery), collectionId)])
        .then(([all, matches]) => {
          if (requestGeneration === generation) render(list, all, matches, source, collectionId)
        })
        .catch(error => console.warn('Facet recovery:', error.message))
    }
  }

  document.addEventListener('collection:rerendered', refresh)
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true })
  else refresh()
})()
